<script setup lang="ts">
    import { __ } from '@/js/stores/expressions';
    import RolesTableCell from './RolesTableCell.vue';
    import TablePaginator from '../dataTable/TablePaginator.vue';
    import TableLoadingOverlay from '../dataTable/TableLoadingOverlay.vue';
    import {
        RolesTableColumn,
        RolesTableItem,
        useRolesTableData,
        mapApiRole,
    } from '@/js/stores/roles-table';
    import { Column, DataTable, PageState } from 'primevue';
    import { useNumericQueryParam, useQueryString } from '@/js/composables/useQueryString';
    import { useTablePageSize } from '@/js/composables/useTablePageSize';
    import { useConfirmationDialog } from '@/js/stores/confirmationDialog';
    import { ref, watchEffect } from 'vue';
    import { useQueryGetRoles, useMutationDeleteRole } from '@/js/services/api/role/roleQueries';
    import SortableColumnHeader from '../dataTable/SortableColumnHeader.vue';
    import { TrashIcon } from '@lucide/vue';
    import BottomScrollFade from '../dataTable/BottomScrollFade.vue';

    const rolesTable = useRolesTableData();
    const { data: rolesData, isLoading } = useQueryGetRoles();
    const { mutateAsync: deleteRoleMutation } = useMutationDeleteRole();

    watchEffect(() => {
        if (rolesData.value) {
            rolesTable.roles = rolesData.value.roles.map(mapApiRole);
        }
    });

    const columns: RolesTableColumn[] = [
        { field: 'name', name: __('roles-table-column-name'), sortable: true },
        { field: 'actions', name: '', sortable: false },
    ];

    const page = useNumericQueryParam('page', {
        startIndex: 0,
        oneBased: true,
        updateMode: 'push',
    });
    const pageSize = useTablePageSize('roles');

    const querystring = useQueryString();
    const paginationChange = async (e: PageState) => {
        const newPage = e.rows === pageSize.value ? e.page + 1 : 1;

        await querystring.setMany({ page: newPage, rows: e.rows }, 'push');
    };

    const confirm = useConfirmationDialog();

    const confirmDeleteRole = async (role: RolesTableItem) => {
        const confirmed = await confirm.openDialog({
            title: __('confirm-delete-role-title'),
            message: __('confirm-delete-role-message', {
                name: role.name,
            }),
            confirmText: __('yes-confirm-delete'),
            cancelText: __('no'),
            confirmIcon: TrashIcon,
        });

        if (confirmed) {
            await deleteRoleMutation(role.id);
        }
    };

    const columnWidths: Record<string, string> = {
        name: '85%',
        actions: '15%',
    };

    const tableRef = ref<HTMLElement | null>(null);
</script>

<template>
    <div ref="tableRef" class="echo-table-wrapper echo-table-wrapper--fill">
        <DataTable
            class="p-datatable-no-top-border row-padding"
            :value="rolesTable.tableData"
            :loading="isLoading"
            data-key="id"
            paginator
            :rows="pageSize"
            :total-records="rolesTable.tableData.length"
            :page="page"
            :first="page * pageSize"
            :scrollable="true"
            scroll-height="flex"
        >
            <template #loadingicon>
                <TableLoadingOverlay />
            </template>
            <Column
                v-for="col in columns"
                :key="col.field"
                :field="col.field"
                :style="{ width: columnWidths[col.field] ?? 'auto' }"
            >
                <template #header>
                    <SortableColumnHeader
                        :name="col.name"
                        :sortable="col.sortable"
                        :active-sort="
                            rolesTable.sortBy === col.field ? rolesTable.sortDirection : undefined
                        "
                        @sort="(dir) => col.sortable && rolesTable.setSort(col.field, dir)"
                    />
                </template>
                <template #body="{ data }">
                    <RolesTableCell :data="data" :column="col.field" @delete="confirmDeleteRole" />
                </template>
            </Column>
            <template #paginatorcontainer="slotProps">
                <TablePaginator
                    :first="slotProps.first"
                    :rows="slotProps.rows"
                    :page="slotProps.page"
                    :total-records="slotProps.totalRecords ?? 0"
                    :page-count="slotProps.pageCount ?? 0"
                    @pagination-change="paginationChange"
                />
            </template>
        </DataTable>
        <BottomScrollFade :container-ref="tableRef" />
    </div>
</template>
